import { useState } from 'react';
import { ApiOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';

import { DataTable, MenuThaoTac, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN } from '../../../lib/dinh-dang';
import { HopTaoKhoa } from '../components/HopTaoKhoa';
import { HopXacNhanXoa } from '../components/HopXacNhanXoa';
import { VungDuLieu } from '../components/VungDuLieu';
import { QUYEN, TRANG_THAI_KHOA } from '../constants';
import { useKhoaTichHop, useThuHoiKhoa, useThuWebhook } from '../hooks/useThamDinh';
import type { KhoaTichHop, TrangThaiKhoa } from '../types';

/**
 * Ba sự kiện webhook của đặc tả mục 13.6, khai ở đây để tài liệu trên màn khớp mã thật.
 *
 * Khoá dịch phẳng chứ không lồng theo mã sự kiện: catalog chỉ nhận chuỗi ở lá, và một khoá chứa dấu chấm
 * (`suKien.phien.cham_xong`) đọc thành ba cấp lồng nhau.
 */
const SU_KIEN = [
  { ma: 'phien.cham_xong', khoa: 'thamDinh.tichHopTaiLieu.suKienChamXong' },
  { ma: 'phien.hoan_tat', khoa: 'thamDinh.tichHopTaiLieu.suKienHoanTat' },
  { ma: 'phien.loi', khoa: 'thamDinh.tichHopTaiLieu.suKienLoi' },
] as const;

/**
 * Tích hợp API (`/client/tham-dinh/tich-hop`), theo giao diện v3.
 *
 * Tài liệu nằm **ngay trên màn**, không ở một trang riêng: người tích hợp mở màn này để lấy khoá, và câu
 * hỏi kế tiếp của họ luôn là "gọi thế nào". Bắt họ đi tìm tài liệu ở chỗ khác là chỗ dễ mất người nhất.
 *
 * Quyền của màn là `danh-muc:*` chứ không phải nhóm riêng — đặc tả mục 14.1 gộp Tích hợp API vào
 * `REVIEW_CATEGORY`, và `module.yaml` giữ nguyên cách gộp đó.
 */
export function TichHopApiView() {
  const { t, locale } = useT();
  const q = useModulePermissions(QUYEN);
  const [moTao, setMoTao] = useState(false);
  const [thuHoi, setThuHoi] = useState<KhoaTichHop | undefined>();

  const ds = useKhoaTichHop();
  const thuHoiKhoa = useThuHoiKhoa();
  const thu = useThuWebhook();

  const cot: TableProps<KhoaTichHop>['columns'] = [
    {
      title: t('thamDinh.khoa.cotTen'),
      dataIndex: 'ten',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-content-primary">{r.ten}</div>
          <div className="truncate font-mono text-2xs text-content-muted">{r.tien_to}…</div>
        </div>
      ),
    },
    {
      title: t('thamDinh.khoa.cotTrangThai'),
      dataIndex: 'trang_thai',
      width: 140,
      render: (v: TrangThaiKhoa) => (
        <Badge variant={TRANG_THAI_KHOA[v].variant}>{t(TRANG_THAI_KHOA[v].khoa)}</Badge>
      ),
    },
    {
      title: t('thamDinh.khoa.cotWebhook'),
      dataIndex: 'webhook_url',
      render: (v: string | null) =>
        v ? <span className="truncate font-mono text-xs">{v}</span> : <span className="text-content-muted">{t('thamDinh.khoa.khongWebhook')}</span>,
    },
    {
      title: t('thamDinh.khoa.cotDungCuoi'),
      dataIndex: 'dung_lan_cuoi',
      width: 150,
      render: (v: string | null) =>
        v ? ngayGioVN(v, locale) : <span className="text-content-muted">{t('thamDinh.khoa.chuaDung')}</span>,
    },
    {
      /*
       * Kết quả lần gửi webhook gần nhất. Cột này là lý do người cấu hình mở màn: một webhook chỉ hỏng lúc
       * cần nhất, và "401" ở đây nói rõ hơn mọi lời giải thích.
       */
      title: t('thamDinh.khoa.cotWebhookKetQua'),
      dataIndex: 'webhook_ket_qua',
      width: 200,
      render: (v: string, r) => {
        if (!r.webhook_url) return <span className="text-content-muted">{KHONG_CO}</span>;
        if (!v) return <span className="text-content-muted">{t('thamDinh.khoa.chuaGuiWebhook')}</span>;
        return (
          <div className="min-w-0">
            <Badge variant={/^2\d\d/.test(v) ? 'success' : 'danger'}>{v.split(' ')[0]}</Badge>
            {r.webhook_lan_cuoi && (
              <p className="mt-0.5 truncate text-2xs text-content-muted">
                {ngayGioVN(r.webhook_lan_cuoi, locale)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      title: t('chung.thaoTac'),
      key: 'thao-tac',
      width: 72,
      align: 'center',
      render: (_, r) => (
        <MenuThaoTac
          tenBanGhi={r.ten}
          muc={[
            {
              khoa: 'thu-webhook',
              nhan: t('thamDinh.khoa.thuWebhook'),
              // Không có webhook thì không thử được: ẩn mục (ADR-UI-0005)
              hien: q.danhMucSua && r.trang_thai === 'hoat_dong' && !!r.webhook_url,
              onChon: () => thu.mutate(r.id),
            },
            {
              khoa: 'thu-hoi',
              nhan: t('thamDinh.khoa.thuHoi'),
              // Khoá đã thu hồi không thu hồi lại được: ẩn mục, không hiện mờ (ADR-UI-0005).
              hien: q.danhMucXoa && r.trang_thai === 'hoat_dong',
              onChon: () => setThuHoi(r),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.thamDinhAi') }, { label: t('nav.muc.tichHopApi') }]}
          titleIcon={<ApiOutlined className="text-primary-600" />}
          title={t('thamDinh.tichHopTieuDe')}
          subtitle={t('thamDinh.tichHopMo')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void ds.refetch()}
              >
                <ReloadOutlined className={ds.isFetching ? 'animate-spin' : ''} />
              </Button>
              {q.danhMucTao && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoTao(true)}>
                  {t('thamDinh.khoa.themMoi')}
                </Button>
              )}
            </>
          }
        />

        <div className="rounded-lg border border-default bg-surface shadow-sm">
          <VungDuLieu
            dangTai={ds.isPending}
            loi={ds.error}
            dulieu={ds.data}
            rong={(ds.data?.items.length ?? 0) === 0}
            onThuLai={() => void ds.refetch()}
            rongIcon={ApiOutlined}
            rongTieuDe={t('thamDinh.khoa.rong')}
            rongMo={t('thamDinh.khoa.rongMo')}
            rongHanhDong={
              q.danhMucTao ? (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoTao(true)}>
                  {t('thamDinh.khoa.themMoi')}
                </Button>
              ) : undefined
            }
          >
            {(du) => (
              <DataTable<KhoaTichHop>
                caption={t('thamDinh.khoa.caption')}
                columns={cot}
                dataSource={du.items}
                pagination={false}
              />
            )}
          </VungDuLieu>
        </div>

        {/* Tài liệu ngay trên màn, như v3. */}
        <div className="grid grid-cols-12 gap-4">
          <Tile span="lg:col-span-6">
            <TileHeader icon={<ApiOutlined />} tone="primary" title={t('thamDinh.tichHopTaiLieu.goiApi')} />
            <TileBody className="space-y-3">
              <p className="text-sm text-content-secondary">{t('thamDinh.tichHopTaiLieu.goiApiMo')}</p>
              <Ma>{`# 1. Liệt kê bộ tiêu chí đã phát hành
curl -H "X-Api-Key: $KHOA" \\
  https://<máy-chủ>/api/v1/m/tham-dinh/cong-khai/bo-tieu-chi

# 2. Gửi hồ sơ (nhiều tệp: lặp lại -F tep=@…)
curl -H "X-Api-Key: $KHOA" \\
  -F bo_id=<id> -F tieu_de=<ten-ho-so> -F ma_tham_chieu=HS-001 \\
  -F tep=@hop-dong.pdf -F tep=@gpkd.pdf \\
  https://<máy-chủ>/api/v1/m/tham-dinh/cong-khai/phien

# 3. Lấy kết quả (hoặc chờ webhook)
curl -H "X-Api-Key: $KHOA" \\
  https://<máy-chủ>/api/v1/m/tham-dinh/cong-khai/phien/<id>`}</Ma>
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-6">
            <TileHeader icon={<ApiOutlined />} tone="teal" title={t('thamDinh.tichHopTaiLieu.webhook')} />
            <TileBody className="space-y-3">
              <dl className="space-y-1 text-sm">
                {SU_KIEN.map(({ ma, khoa }) => (
                  <div key={ma} className="flex flex-wrap gap-2">
                    <dt className="font-mono text-xs text-content-primary">{ma}</dt>
                    <dd className="min-w-0 flex-1 text-content-secondary">{t(khoa)}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-sm text-content-secondary">{t('thamDinh.tichHopTaiLieu.webhookMo')}</p>
              <Ma>{`// Node.js — kiểm chữ ký webhook bằng bí mật whsec_…
const ky = 'sha256=' + crypto
  .createHmac('sha256', BI_MAT)
  .update(rawBody)
  .digest('hex');
const hopLe = crypto.timingSafeEqual(
  Buffer.from(ky),
  Buffer.from(req.headers['x-foxai-signature']),
);`}</Ma>
            </TileBody>
          </Tile>
        </div>
      </div>

      <HopTaoKhoa open={moTao} onClose={() => setMoTao(false)} />

      <HopXacNhanXoa
        open={Boolean(thuHoi)}
        onClose={() => {
          setThuHoi(undefined);
          thuHoiKhoa.reset();
        }}
        tieuDe={t('thamDinh.khoa.thuHoiHoiTieuDe')}
        than={t('thamDinh.khoa.thuHoiHoiThan', { ten: thuHoi?.ten ?? '' })}
        nhanXacNhan={t('thamDinh.khoa.thuHoiXacNhan')}
        dangChay={thuHoiKhoa.isPending}
        loi={thuHoiKhoa.error}
        onXacNhan={() =>
          thuHoi && thuHoiKhoa.mutate({ id: thuHoi.id, ten: thuHoi.ten }, { onSuccess: () => setThuHoi(undefined) })
        }
      />
    </div>
  );
}

/** Khối mã ví dụ; cuộn ngang thay vì xuống dòng để lệnh curl đọc được nguyên dòng. */
function Ma({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded border border-default bg-subtle p-3 text-2xs leading-relaxed text-content-secondary">
      <code>{children}</code>
    </pre>
  );
}
